import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KanbanAddOrigin, useKanbanApi } from "@/utils/kanban-api";
import { useAuth } from "@/contexts/AuthContext";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { invalidateVagaKanban } from "@/hooks/use-enviar-freelancers-para-vaga";
import { useToast } from "@/hooks/use-toast";
import { FreelancerStatus } from "@/types/freelancer-rating";
import { CheckCircle2, EyeOff, FolderOpen, Building2, Briefcase, RefreshCw } from "lucide-react";
import { DropCandidateModal } from "@/components/vagas/kanban/DropCandidateModal";
import { FreelancerProjetosModal } from "@/components/vagas/kanban/FreelancerProjetosModal";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { formatMotivosLista, hasMotivos, parseDropReasons } from "@/utils/motivosUtils";
import { createLocalDate, calcularDiasRestantes } from "@/utils/dateUtils";
import { projectArea } from "@/utils/project-area";
import { reportUserError } from "@/lib/report-error";

interface KanbanStageSelectorProps {
  vagaId: string;
  freelancerId: string;
  onSuccess?: () => void;
  freelancerNome?: string;
  freelancerEmail?: string;
}

export function KanbanStageSelector({
  vagaId,
  freelancerId,
  onSuccess,
  freelancerNome,
  freelancerEmail,
}: KanbanStageSelectorProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const kanbanApi = useKanbanApi();
  const queryClient = useQueryClient();
  const invalidateFreelancers = useFreelancersInvalidate();

  const { data: etapas = [], isLoading } = useQuery({
    queryKey: ["kanban-etapas", vagaId],
    queryFn: () => kanbanApi.getEtapas(vagaId),
  });

  // Buscar todos os cards incluindo os com drop-in-place
  const { data: cardsData = [], isLoading: isLoadingCards } = useQuery({
    queryKey: ["kanban-cards-all", vagaId],
    queryFn: () => kanbanApi.getCards(vagaId, true), // incluir drop-in-place
  });

  // Buscar histórico de drop se o freelancer estiver na etapa de drop
  const freelancerCard = cardsData.find(
    (card) => card.freelancer_id === freelancerId
  );

  const etapaAtual = freelancerCard ? etapas.find(
    (e) => e.id === freelancerCard.etapa_atual_id
  ) : null;

  // Buscar histórico de drop se estiver na etapa de drop OU se tiver drop-in-place.
  // Chave "single" (1 posição) — distinta da chave de batch da aba do kanban
  // (["kanban-historico-drop", ids_concatenados]) pra evitar colisão de cache.
  const { data: historicoDrop } = useQuery({
    queryKey: ["kanban-historico-single", freelancerCard?.id],
    queryFn: () => {
      if (!freelancerCard?.id) return null;
      return kanbanApi.getHistorico(freelancerCard.id);
    },
    enabled: !!freelancerCard && (etapaAtual?.tipo === "drop" || freelancerCard.drop_tipo === "in_place"),
    select: (data) => {
      if (!data) return null;
      // Buscar o último movimento de drop ou drop_in_place
      const dropMovimento = data.find(
        (h) => h.tipo_movimento === "drop" || h.tipo_movimento === "drop_in_place"
      );
      return dropMovimento || null;
    },
  });

  // Função auxiliar para renderizar motivos de drop (array)
  const renderMotivos = (motivos?: unknown) => {
    const lista = parseDropReasons(motivos);
    if (lista.length === 0) return null;

    return (
      <div className="text-slate-600">
        <span className="text-slate-500">
          {lista.length > 1 ? "Motivos:" : "Motivo:"}
        </span>
        <div className="mt-1">
          {formatMotivosLista(lista, lista.length === 1 ? "font-medium" : "")}
        </div>
      </div>
    );
  };

  // Buscar projetos em andamento do freelancer
  const { data: projetosFreelancer = [] } = useQuery({
    queryKey: ["projetos-andamento-freelancer", freelancerId],
    queryFn: async () => {
      const hoje = new Date().toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from("controle_projetos")
        .select("id, freelancer_id, area_atuacao, area_canonica, data_inicio, data_conclusao, agencia:agencias(nome)")
        .eq("freelancer_id", freelancerId)
        .is("deleted_at", null)
        .or(`data_conclusao.is.null,data_conclusao.gte.${hoje}`)
        .order("data_inicio", { ascending: false });
      
      if (error) {
        console.error("Erro ao buscar projetos:", error);
        return [];
      }
      
      return data || [];
    },
    staleTime: 60000, // Cache por 1 minuto
  });

  const [selectedEtapaId, setSelectedEtapaId] = useState<string>("");
  const [isAdding, setIsAdding] = useState(false);
  const [showDropModal, setShowDropModal] = useState(false);
  const [etapaDestinoDrop, setEtapaDestinoDrop] = useState<string | null>(null);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [showReactivateForm, setShowReactivateForm] = useState(false);
  const [observacoesReativacao, setObservacoesReativacao] = useState("");
  const [isReactivating, setIsReactivating] = useState(false);

  // ========== FUNÇÕES AUXILIARES PARA PROJETOS ==========
  
  const getProjetoInfo = () => {
    const projetoAtual = projetosFreelancer[0];
    const totalProjetos = projetosFreelancer.length;
    
    const diasRestantes = projetoAtual?.data_conclusao ? calcularDiasRestantes(projetoAtual.data_conclusao) : null;
    const proximoDeConcluir = diasRestantes !== null && diasRestantes >= 0 && diasRestantes < 10;

    return {
      projetoAtual,
      totalProjetos,
      diasRestantes,
      proximoDeConcluir
    };
  };

  const renderProjetoInfo = () => {
    const { projetoAtual, totalProjetos, diasRestantes, proximoDeConcluir } = getProjetoInfo();

    return (
      <>
        <div 
          className={`p-2 bg-slate-50 rounded-md border border-slate-200 transition-colors ${
            totalProjetos > 0 ? 'cursor-pointer hover:bg-slate-100' : ''
          }`}
          onClick={(e) => {
            if (totalProjetos > 0) {
              e.stopPropagation();
              setShowProjectsModal(true);
            }
          }}
          title={totalProjetos > 0 ? "Clique para ver todos os projetos" : ""}
        >
          {totalProjetos > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <FolderOpen className="h-3.5 w-3.5 shrink-0 text-green-500" />
                <span className="text-green-700 font-medium">
                  {totalProjetos > 1 ? `${totalProjetos} Projetos` : '1 Projeto'}
                </span>
                {diasRestantes !== null && (
                  <Badge 
                    variant="outline" 
                    className={`text-[10px] px-1 py-0 h-4 ${
                      proximoDeConcluir 
                        ? 'bg-orange-50 text-orange-700 border-orange-300' 
                        : 'bg-slate-50 text-slate-600 border-slate-300'
                    }`}
                  >
                    {diasRestantes}d
                  </Badge>
                )}
              </div>
              {projetoAtual?.agencia?.nome && (
                <div className="flex items-center gap-2 text-xs">
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                  <span className="text-blue-700 font-medium truncate">
                    {projetoAtual.agencia.nome}
                  </span>
                </div>
              )}
              <div className="flex items-start gap-2 text-xs text-slate-600">
                <Briefcase className="h-3.5 w-3.5 shrink-0 text-slate-400 mt-0.5" />
                <span className="line-clamp-2">{projectArea(projetoAtual)}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <FolderOpen className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="text-slate-500 font-medium">Sem Projeto</span>
              </div>
              <div className="text-xs text-slate-400">
                Nenhum projeto ativo no momento
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-200" />
      </>
    );
  };

  const handleAddToKanban = async () => {
    if (!selectedEtapaId || !user?.id) {
      toast({
        title: "Erro",
        description: "Selecione uma etapa para adicionar o candidato.",
        variant: "destructive",
      });
      return;
    }

    setIsAdding(true);

    try {
      // Buscar a etapa selecionada
      const etapa = etapas.find((e) => e.id === selectedEtapaId);
      if (!etapa) {
        throw new Error("Etapa não encontrada");
      }

      // Contar quantos cards já estão na etapa para definir a ordem
      const cardsNaEtapa = cardsData.filter(
        (card) => card.etapa_atual_id === selectedEtapaId
      );
      const novaOrdem = cardsNaEtapa.length;

      // Adicionar candidato ao kanban
      await kanbanApi.addCandidate(vagaId, freelancerId, selectedEtapaId, {
        origem: KanbanAddOrigin.ListaVaga,
        ordem: novaOrdem,
        usuarioId: user?.id,
      });
      invalidateVagaKanban(queryClient, vagaId);

      toast({
        title: "Sucesso",
        description: `Candidato adicionado à etapa "${etapa.nome}"!`,
      });

      setSelectedEtapaId("");
      onSuccess?.();
    } catch (caught) {
      const error = caught as { code?: string; message?: string } | null;
      console.error("Erro ao adicionar candidato ao kanban:", error);
      const isDuplicate = error?.code === "23505";
      toast({
        title: "Erro",
        description: isDuplicate
          ? "Este freelancer já está nesta vaga."
          : "Não foi possível adicionar o candidato ao kanban. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleDropConfirmed = async (data: { motivos: string[]; observacoes?: string; status?: FreelancerStatus }) => {
    if (!etapaDestinoDrop || !freelancerCard) return;

    setIsAdding(true);

    try {
      // Primeiro, fazer o drop no kanban
      await kanbanApi.dropCard(
        freelancerCard.id,
        etapaDestinoDrop,
        data.motivos,
        data.observacoes,
        user?.id
      );
      invalidateVagaKanban(queryClient, vagaId);
      queryClient.invalidateQueries({ queryKey: ["kanban-historico-single", freelancerCard.id] });

      // Se um status foi fornecido (motivo relacionado a disponibilidade), atualizar o status do freelancer
      if (data.status && freelancerCard.freelancer_id) {
        const hoje = new Date().toISOString().split('T')[0]; // Data de hoje no formato YYYY-MM-DD
        await kanbanApi.updateFreelancerDisponibilidade(
          freelancerCard.freelancer_id,
          hoje,
          data.status,
          data.observacoes,
          user?.id
        );
        // Invalidar queries relacionadas ao freelancer para atualizar os dados
        queryClient.invalidateQueries({ queryKey: ["disponibilidade"] });
        queryClient.invalidateQueries({ queryKey: ["kanban-cards", vagaId] });
        // Invalidar cache de freelancers para atualizar a listagem
        invalidateFreelancers();
      }

      toast({
        title: "Sucesso",
        description: "Candidato descartado com sucesso!",
      });

      setSelectedEtapaId("");
      setEtapaDestinoDrop(null);
      setShowDropModal(false);
      onSuccess?.();
    } catch (error) {
      console.error("Erro ao descartar candidato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível descartar o candidato. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleReactivate = async () => {
    if (!freelancerCard) return;

    setIsReactivating(true);

    try {
      await kanbanApi.reactivateCardInPlace(
        freelancerCard.id,
        observacoesReativacao || undefined,
        user?.id
      );

      invalidateVagaKanban(queryClient, vagaId);
      queryClient.invalidateQueries({ queryKey: ["kanban-historico-single", freelancerCard.id] });
      invalidateFreelancers();

      toast({
        title: "Sucesso",
        description: "Candidato reativado com sucesso!",
      });

      setShowReactivateForm(false);
      setObservacoesReativacao("");
      onSuccess?.();
    } catch (error) {
      console.error("Erro ao reativar candidato:", error);
      toast({
        title: "Erro",
        description: "Não foi possível reativar o candidato. " + reportUserError(error, { source: "kanban" }).description,
        variant: "destructive",
      });
    } finally {
      setIsReactivating(false);
    }
  };

  // Sem os cards ainda, quem já está na vaga pareceria fora dela.
  if (isLoading || isLoadingCards) {
    return (
      <div className="flex items-center justify-center py-2 text-sm text-slate-500">
        Carregando etapas...
      </div>
    );
  }

  if (freelancerCard) {
    const etapaAtual = etapas.find(
      (e) => e.id === freelancerCard.etapa_atual_id
    );
    
    const isDropInPlace = freelancerCard.is_dropped && freelancerCard.drop_tipo === "in_place";

    // Se estiver com drop-in-place, mostrar informação sem permitir ações
    if (isDropInPlace) {
      return (
        <div className="flex flex-col gap-3 bg-orange-50 px-4 py-3 rounded-b-lg">
          {renderProjetoInfo()}
          <div className="flex items-start gap-2">
            <EyeOff className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-bold text-orange-700">
                Candidato Descartado
              </span>
              <div className="mt-1 text-xs text-slate-600">
                <span className="text-slate-500">Na etapa: </span>
                <span className="font-medium">{etapaAtual?.nome || "Etapa desconhecida"}</span>
              </div>
            </div>
          </div>
          
          {historicoDrop && (
            <div className="mt-2 space-y-1.5 text-xs bg-white border border-orange-200 rounded-md p-2">
              {hasMotivos(historicoDrop.motivo_drop) && (
                <div className="text-slate-600">
                  {renderMotivos(historicoDrop.motivo_drop)}
                </div>
              )}
              {historicoDrop.observacoes && (
                <div className="text-slate-600">
                  <span className="text-slate-500">Observações: </span>
                  <span>{historicoDrop.observacoes}</span>
                </div>
              )}
              {historicoDrop.usuario && (
                <div className="text-slate-600">
                  <span className="text-slate-500">Descartado por: </span>
                  <span className="font-medium">
                    {historicoDrop.usuario.full_name || historicoDrop.usuario.username || "Usuário desconhecido"}
                  </span>
                </div>
              )}
            </div>
          )}

          {showReactivateForm ? (
            <div className="mt-1 space-y-2 bg-white border border-orange-200 rounded-md p-2">
              <Textarea
                placeholder="Observações (opcional)"
                value={observacoesReativacao}
                onChange={(e) => setObservacoesReativacao(e.target.value)}
                rows={2}
                className="bg-white text-sm"
                disabled={isReactivating}
              />
              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowReactivateForm(false);
                    setObservacoesReativacao("");
                  }}
                  disabled={isReactivating}
                >
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleReactivate} disabled={isReactivating}>
                  {isReactivating && <RefreshCw className="h-3 w-3 mr-1 animate-spin" />}
                  Confirmar Reativação
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowReactivateForm(true)}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-2" />
              Reativar Candidato
            </Button>
          )}

          {/* Modal de Projetos */}
          <FreelancerProjetosModal
            open={showProjectsModal}
            onOpenChange={setShowProjectsModal}
            freelancerNome={freelancerNome || "Freelancer"}
            projetos={projetosFreelancer}
          />
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-3 bg-background px-4 py-3 rounded-b-lg">
          {renderProjetoInfo()}
          <div className="flex items-start gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full shrink-0 mt-1"
              style={{ backgroundColor: etapaAtual?.cor || "#8B5CF6" }}
            />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-slate-700">
                Etapa atual: {etapaAtual?.nome || "Etapa desconhecida"}
              </span>
              {etapaAtual?.tipo === "drop" && historicoDrop && (
                <div className="mt-2 space-y-1 text-xs">
                  {hasMotivos(historicoDrop.motivo_drop) && (
                    <div className="text-slate-600">
                      {renderMotivos(historicoDrop.motivo_drop)}
                    </div>
                  )}
                  {historicoDrop.usuario && (
                    <div className="text-slate-600">
                      <span className="text-slate-500">Descartado por: </span>
                      <span className="font-medium">
                        {historicoDrop.usuario.full_name || historicoDrop.usuario.username || "Usuário desconhecido"}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        
        {/* Seletor para mover entre etapas */}
        <Select
          value={selectedEtapaId}
          onValueChange={setSelectedEtapaId}
          disabled={isAdding}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue placeholder="Mover para outra etapa" />
          </SelectTrigger>
          <SelectContent>
            {etapas
              .filter((etapa) => etapa.id !== freelancerCard.etapa_atual_id) // Não mostrar a etapa atual
              .map((etapa) => (
                <SelectItem key={etapa.id} value={etapa.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: etapa.cor }}
                    />
                    <span>{etapa.nome}</span>
                  </div>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        {selectedEtapaId && (
          <Button
            variant="default"
            size="sm"
            onClick={async () => {
              if (!selectedEtapaId || !user?.id) return;
              
              const etapaDestino = etapas.find((e) => e.id === selectedEtapaId);
              if (!etapaDestino) {
                return;
              }

              // Se a etapa destino for do tipo "drop", abrir modal de drop
              if (etapaDestino.tipo === "drop") {
                setEtapaDestinoDrop(selectedEtapaId);
                setShowDropModal(true);
                return;
              }

              // Caso contrário, mover normalmente
              setIsAdding(true);
              try {
                const cardsNaEtapa = cardsData.filter(
                  (card) => card.etapa_atual_id === selectedEtapaId
                );
                const novaOrdem = cardsNaEtapa.length;

                await kanbanApi.moveCard(
                  freelancerCard.id,
                  selectedEtapaId,
                  novaOrdem,
                  undefined,
                  user?.id
                );
                invalidateVagaKanban(queryClient, vagaId);

                toast({
                  title: "Sucesso",
                  description: `Candidato movido para "${etapaDestino.nome}"!`,
                });

                setSelectedEtapaId("");
                onSuccess?.();
              } catch (error) {
                console.error("Erro ao mover candidato:", error);
                toast({
                  title: "Erro",
                  description: "Não foi possível mover o candidato. " + reportUserError(error, { source: "kanban" }).description,
                  variant: "destructive",
                });
              } finally {
                setIsAdding(false);
              }
            }}
            disabled={isAdding}
            className="w-full"
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            {isAdding ? "Movendo..." : "Mover para etapa"}
          </Button>
        )}
      </div>

      {/* Modal de Drop */}
      {freelancerCard && (
        <DropCandidateModal
          open={showDropModal}
          onOpenChange={(open) => {
            setShowDropModal(open);
            if (!open) {
              setEtapaDestinoDrop(null);
              setSelectedEtapaId("");
            }
          }}
          candidate={freelancerCard}
          onConfirm={handleDropConfirmed}
          isLoading={isAdding}
        />
      )}

      {/* Modal de Projetos */}
      <FreelancerProjetosModal
        open={showProjectsModal}
        onOpenChange={setShowProjectsModal}
        freelancerNome={freelancerNome || "Freelancer"}
        projetos={projetosFreelancer}
      />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3 bg-background px-4 py-3 rounded-b-lg">
      {renderProjetoInfo()}
      <Select
        value={selectedEtapaId}
        onValueChange={setSelectedEtapaId}
        disabled={isAdding}
      >
        <SelectTrigger className="h-8 text-sm">
          <SelectValue placeholder="Selecione uma etapa" />
        </SelectTrigger>
        <SelectContent>
          {etapas
            .filter((etapa) => etapa.tipo !== "drop") // Não permitir adicionar diretamente na etapa de drop
            .map((etapa) => (
              <SelectItem key={etapa.id} value={etapa.id}>
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: etapa.cor }}
                  />
                  <span>{etapa.nome}</span>
                </div>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
      <Button
        variant="default"
        size="sm"
        onClick={handleAddToKanban}
        disabled={!selectedEtapaId || isAdding}
        className="w-full"
      >
        <CheckCircle2 className="h-4 w-4 mr-2" />
        {isAdding ? "Adicionando..." : "Adicionar ao Kanban"}
      </Button>

      {/* Modal de Projetos */}
      <FreelancerProjetosModal
        open={showProjectsModal}
        onOpenChange={setShowProjectsModal}
        freelancerNome={freelancerNome || "Freelancer"}
        projetos={projetosFreelancer}
      />
    </div>
  );
}

